/**
 * Computed Fields Enrichment Utility
 *
 * Adds derived per-row columns to chart data arrays using safe mathjs evaluation.
 * Used by LineChart, BarChart, and AreaChart to compute fields like "profit = revenue - cost".
 */

import {
  create,
  evaluateDependencies,
  addDependencies,
  subtractDependencies,
  multiplyDependencies,
  divideDependencies,
  unaryMinusDependencies,
  unaryPlusDependencies,
  minDependencies,
  maxDependencies,
  absDependencies,
  roundDependencies,
  floorDependencies,
  ceilDependencies,
  modDependencies,
} from 'mathjs/number';
import type { ComputedField } from '../types/chartDataSource';

// ALLOWLIST: Restricted mathjs instance with ONLY safe arithmetic functions.
// Follows the same pattern as ExpressionEvaluator.ts.
const restrictedMath = create({
  evaluateDependencies,
  addDependencies,
  subtractDependencies,
  multiplyDependencies,
  divideDependencies,
  unaryMinusDependencies,
  unaryPlusDependencies,
  minDependencies,
  maxDependencies,
  absDependencies,
  roundDependencies,
  floorDependencies,
  ceilDependencies,
  modDependencies,
});

/**
 * Adds computed columns to each row of a data array.
 * Uses a restricted mathjs instance for safe evaluation with the row's fields as scope.
 *
 * @param data - Raw data array from query template
 * @param computedFields - Field definitions with expressions
 * @returns Enriched data array with computed columns added
 */
export function enrichDataWithComputedFields(
  data: Record<string, unknown>[],
  computedFields: ComputedField[] | undefined,
): Record<string, unknown>[] {
  if (!computedFields?.length || !data.length) return data;

  // Track warned fields to throttle console output (once per field per call)
  const warned = new Set<string>();

  return data.map(row => {
    const enrichedRow = { ...row };
    for (const field of computedFields) {
      try {
        // Build numeric scope from row fields
        const scope: Record<string, number> = {};
        for (const [key, val] of Object.entries(row)) {
          const num = Number(val);
          if (!isNaN(num)) scope[key] = num;
        }
        const result = restrictedMath.evaluate(field.expression, scope);
        enrichedRow[field.name] = typeof result === 'number' && isFinite(result) ? result : null;
      } catch (e) {
        enrichedRow[field.name] = null;
        if (!warned.has(field.name)) {
          warned.add(field.name);
          console.warn(`[ComputedField] Failed to evaluate "${field.expression}" for field "${field.name}":`, e);
        }
      }
    }
    return enrichedRow;
  });
}
